import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import VideoCard from '@/components/feature/VideoCard';
import HeroCarousel from './components/HeroCarousel';
import { videos, categoryList } from '@/mocks/videos';

function SectionTitle({
  title,
  subtitle,
  to,
}: {
  title: string;
  subtitle: string;
  to?: string;
}) {
  return (
    <div className="mb-6 flex items-end justify-between gap-4">
      <div>
        <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
          <span className="h-5 w-1.5 rounded-full bg-primary-500 md:h-6" />
          {title}
        </h2>
        <p className="mt-1 pl-3.5 text-xs text-foreground-500 md:text-sm">{subtitle}</p>
      </div>
      {to && (
        <Link
          to={to}
          className="flex shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
        >
          查看全部
          <i className="ri-arrow-right-line" />
        </Link>
      )}
    </div>
  );
}

export default function Home() {
  const featured = [...videos].sort((a, b) => b.views - a.views).slice(0, 5);
  const hot = [...videos].sort((a, b) => b.views - a.views).slice(0, 10);
  const latest = [...videos]
    .sort((a, b) => (a.uploadDate < b.uploadDate ? 1 : -1))
    .slice(0, 8);

  return (
    <Layout>
      <HeroCarousel items={featured} />

      <div className="mx-auto w-full px-4 py-10 md:px-8 md:py-14">
        {/* Category quick entry */}
        <div className="mb-12 flex gap-4 overflow-x-auto pb-2 no-scrollbar sm:grid sm:grid-cols-3 sm:overflow-visible sm:pb-0">
          {categoryList.map((c) => (
            <Link
              key={c.key}
              to={`/category/${c.key}`}
              className="group relative flex min-w-[280px] cursor-pointer items-center gap-4 overflow-hidden rounded-lg border border-background-200/70 bg-background-50 p-4 transition-all duration-300 hover:-translate-y-1 hover:border-primary-300 sm:min-w-0 md:p-5"
            >
              <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-md bg-background-200 md:h-20 md:w-20">
                <img
                  src={c.cover}
                  alt={`${c.name}分类`}
                  title={`${c.name}视频 - 涩涩视频`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950 md:text-lg">
                  <i className={`${c.icon} text-primary-500`} />
                  {c.name}
                </h3>
                <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-foreground-500 md:text-sm">
                  {c.desc}
                </p>
              </div>
              <i className="ri-arrow-right-s-line shrink-0 text-2xl text-foreground-300 transition-colors group-hover:text-primary-500" />
            </Link>
          ))}
        </div>

        {/* Hot videos */}
        <section className="mb-14">
          <SectionTitle title="热门视频精选" subtitle="全站播放量最高的内容，大家都在看" to="/category/movie" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
            {hot.map((v, i) => (
              <VideoCard key={v.id} video={v} rank={i + 1} />
            ))}
          </div>
        </section>

        {/* Latest */}
        <section>
          <SectionTitle title="最新上传" subtitle="按上传时间倒序排列，第一时间发现新内容" to="/category/short" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4">
            {latest.map((v) => (
              <VideoCard key={v.id} video={v} />
            ))}
          </div>
        </section>
      </div>

      {/* CTA band */}
      <section className="mx-auto w-full px-4 pb-4 md:px-8">
        <div className="flex flex-col items-center justify-between gap-5 rounded-lg bg-accent-500 px-6 py-8 text-center md:flex-row md:px-10 md:text-left">
          <div>
            <h3 className="font-heading text-xl font-bold text-background-50 md:text-2xl">
              找不到想看的内容？
            </h3>
            <p className="mt-2 text-sm text-background-100/90">
              输入关键词，涩涩视频帮你从海量内容中快速定位感兴趣的精彩视频。
            </p>
          </div>
          <Link
            to="/search"
            className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-6 py-3 text-sm font-medium text-accent-700 transition-colors hover:bg-background-100"
          >
            <i className="ri-search-line text-lg" />
            立即搜索
          </Link>
        </div>
      </section>
    </Layout>
  );
}